import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import CurrencyInput from 'components/field/CurrencyInput';
import EnumSelect from 'components/field/EnumSelect';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, ENUMS, FINANCE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const companyAutocompleteColumns = [
	{ accessor: 'ID', width: 200 },
	{ accessor: 'Name', width: 800 }
];

const orderIDAutocompleteColumns = [{ accessor: 'orderID', width: 200 }];

const ClientTransactionModal = NiceModal.create(() => {
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const modal = useModal();
	const defaultColSpan = {
		md: 6
	};

	const handleCloseModal = useCallback(() => {
		modal.remove();
	}, []);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					companyID: yup.number().nullable().required(),
					orderID: yup.number().nullable(),
					amount: yup.number().nullable().required(),
					transactionType: yup.string().required(),
					descriptionType: yup
						.string()
						.nullable()
						.test('description-or-descriptionType', 'alert.requiredField', (value, context) => {
							const { description } = context.parent;
							return description || (typeof value === 'string' && value.length > 0);
						}),
					description: yup
						.string()
						.nullable()
						.test('description-or-descriptionType', 'alert.requiredField', (value, context) => {
							const { descriptionType } = context.parent;
							return descriptionType || (typeof value === 'string' && value.length > 0);
						})
				})
				.required(),
		[]
	);

	const fillForm = useCallback(async () => {
		const defaultValues = {
			companyID: null,
			orderID: null,
			amount: null,
			transactionType: null,
			descriptionType: null,
			description: null
		};
		return defaultValues;
	}, []);

	const formMethods = useForm({
		defaultValues: fillForm,
		resolver: yupResolver(schema)
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, errors, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isFormLoading || isSubmitting;

	const onTransactionTypeChange = (value) => {
		setValue('transactionType', value, {
			shouldValidate: true,
			shouldDirty: true
		});

		if (value === 'chargeFromCreditCard') {
			setValue('description', null, {
				shouldValidate: true,
				shouldDirty: true
			});
		} else {
			setValue('descriptionType', null, {
				shouldValidate: true,
				shouldDirty: true
			});
		}
	};

	const onSubmit = async (data) => {
		try {
			const response = await axiosPrivate.post(FINANCE.ADD_CLIENT_TRANSACTIONS, data);

			const { status } = response;

			if (status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.CLIENT_TRANSACTIONS));
				showSuccess();
				handleCloseModal();
			} else {
				showError({
					message: 'error.FN014'
				});
				AppLogger.error(response);
			}
		} catch (error) {
			showError({
				message: error.response?.data.code || error.response?.data.message
			});
			AppLogger.error(error);
		}
	};
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	return (
		<Modal fullwidth maxWidth="medium" className="modal-client-transaction">
			<ModalTitle>
				<Typography variant="p1">{'addClientTransaction'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="company" isLoading={isLoading} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.CLIENTS}
							name="companyID"
							value={watch('companyID')}
							setValue={(value) => {
								setValue('companyID', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});

								setValue('orderID', null);
							}}
							columns={companyAutocompleteColumns}
							selectionLabelAccessor={['ID', 'Name']}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="orderNo">
						<AutoComplete
							name="orderID"
							autoCompleteName={AUTOCOMPLETE.ORDERS_BY_COMPANY}
							columns={orderIDAutocompleteColumns}
							selectionLabelAccessor={['orderID']}
							value={watch('orderID')}
							disabled={!watch('companyID')}
							setValue={(value) => {
								setValue('orderID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
							}}
							filterObject={{
								userCompanyID: watch('companyID')
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="amount" isLoading={isLoading} required>
						<CurrencyInput
							name="amount"
							value={watch('currency')}
							setValue={(value) =>
								setValue(`currency`, value, { shouldValidate: true, shouldDirty: true })
							}
							currencyErrorMessage={errors?.currency?.message}
							placeholder="enterAmount"
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="actionType" isLoading={isLoading} required>
						<EnumSelect
							name="transactionType"
							value={watch('transactionType')}
							setValue={(value) => onTransactionTypeChange(value)}
							enumName={ENUMS.CLIENT_TRANSACTION_ACTION_TYPE}
							valueRemovable={false}
							searchable={false}
							placeholder="selectActionType"
						/>
					</FormItem>
					{watch('transactionType') === 'chargeFromCreditCard' ? (
						<FormItem label="description" required>
							<EnumSelect
								name="descriptionType"
								searchable={false}
								enumName={ENUMS.INVOICE_CHECKER_DIFFERENCE_TYPES}
								setValue={(value) => {
									setValue('descriptionType', value, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
								value={watch('descriptionType')}
							/>
						</FormItem>
					) : (
						<FormItem label="description" isLoading={isLoading} required>
							<TextArea name="description" rows="3" />
						</FormItem>
					)}
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label="save"
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default ClientTransactionModal;
